import React from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { Button } from '@roger/ui-components';
import type { ImportedMedicationProfileResponse } from '../../services/api';
import {
  IMPORTED_MEDICATIONS_NOT_SCREENED_COPY,
  OPEN_IMPORTER_TOOL_LABEL,
  convertedCountCopy,
  discontinuedScopeCopy,
} from './importedChartLineCopy';
import ImportedConversionsNote from './ImportedConversionsNote';
import { convertedLines } from './importedChartLines';

/**
 * The banner above the medication list when raw Kinnser lines are present.
 *
 * It carries the one sentence this whole feature exists to make true: the
 * imported lines have never been screened for interactions. A clinician
 * scanning a single combined list would otherwise assume the screening covered
 * everything in it, which is precisely the wrong assumption to let stand.
 *
 * The scope qualifier sits beside it because an empty discontinued list means
 * different things depending on Kinnser's PAST30DAYS flag, and the reader
 * cannot know which without being told.
 *
 * Renders nothing when no line is outstanding. Once every line has been ruled
 * on, there is nothing unscreened left to warn about. Lines the import coded on
 * a certain match (EMR-2408) are coded medications, so they are screened and
 * never counted here; while any exist, a quiet note keeps the tool reachable.
 */
const ImportedMedicationsNotice: React.FC<{
  profile: ImportedMedicationProfileResponse | null | undefined;
  outstandingCount: number;
  /** Absent when the viewer lacks the reconcile permission. */
  onOpenImporter?: () => void;
}> = ({ profile, outstandingCount, onOpenImporter }) => {
  if (!profile) {
    return null;
  }
  const convertedCount = convertedLines(profile.lines).length;
  if (outstandingCount === 0) {
    return (
      <ImportedConversionsNote
        convertedCount={convertedCount}
        kind='medication'
        onOpenImporter={onOpenImporter}
      />
    );
  }

  return (
    <div
      className='mb-4 rounded-md border border-amber-200 bg-amber-50 p-3'
      data-testid='imported-medications-notice'
    >
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div className='flex gap-2'>
          <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
          <div className='space-y-1'>
            <p className='text-sm text-amber-900'>
              {IMPORTED_MEDICATIONS_NOT_SCREENED_COPY}
            </p>
            <p
              className='text-xs text-amber-800'
              data-testid='imported-discontinued-scope'
            >
              {discontinuedScopeCopy(profile.discontinued_meds_past_30_days)}
            </p>
            {convertedCount > 0 && (
              <p className='text-xs text-amber-800'>
                {convertedCountCopy(convertedCount, 'medication')}
              </p>
            )}
          </div>
        </div>
        {onOpenImporter && (
          <Button
            size='sm'
            variant='outline'
            className='shrink-0 self-start'
            onClick={onOpenImporter}
            data-testid='open-importer-tool'
          >
            <Download className='h-3.5 w-3.5 mr-1.5' />
            {OPEN_IMPORTER_TOOL_LABEL}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ImportedMedicationsNotice;
